// Free, on-device pronunciation for Strong's / original-language words using the
// browser's Web Speech API. Speaks the ORIGINAL Hebrew/Greek script when the device
// has a matching voice; otherwise falls back to reading the transliteration so there
// is ALWAYS audio. No cost, no network.

let _voicesReady = null;

function ensureVoices() {
  if (!("speechSynthesis" in window)) return Promise.resolve([]);
  const synth = window.speechSynthesis;
  const now = synth.getVoices();
  if (now && now.length) return Promise.resolve(now);
  if (_voicesReady) return _voicesReady;
  _voicesReady = new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve(synth.getVoices() || []);
    };
    try {
      synth.addEventListener("voiceschanged", finish, { once: true });
    } catch (e) {
      /* older browsers */
    }
    setTimeout(finish, 1200);
  });
  return _voicesReady;
}

function isHebrewEntry(entry) {
  const num = (entry?.strongs_number || "").toUpperCase();
  if (num.startsWith("G")) return false; // Greek
  return true; // Hebrew is the default (H… or unknown)
}

// Whether the device can natively voice this word's language.
export async function canSpeakNative(entry) {
  const voices = await ensureVoices();
  const prefix = isHebrewEntry(entry) ? "he" : "el";
  return voices.some((v) => (v.lang || "").toLowerCase().startsWith(prefix));
}

export async function pronounceStrongs(entry, onEnd) {
  if (!("speechSynthesis" in window)) {
    onEnd?.();
    return { supported: false, native: false, spoken: "" };
  }
  const synth = window.speechSynthesis;
  const voices = await ensureVoices();
  synth.cancel();

  const hebrew = isHebrewEntry(entry);
  const prefix = hebrew ? "he" : "el";
  const native = voices.find((v) => (v.lang || "").toLowerCase().startsWith(prefix));

  let text;
  let lang;
  let usedNative;
  if (native && (entry.original || "").trim()) {
    text = entry.original.trim();
    lang = hebrew ? "he-IL" : "el-GR";
    usedNative = true;
  } else {
    text = (entry.transliteration || entry.word || entry.original || "").trim();
    lang = "en-US";
    usedNative = false;
  }
  if (!text) {
    onEnd?.();
    return { supported: true, native: false, spoken: "" };
  }

  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  if (usedNative) u.voice = native;
  u.rate = 0.7; // slow it down — this is for learning the sound
  u.onend = () => onEnd?.();
  u.onerror = () => onEnd?.();
  synth.speak(u);
  return { supported: true, native: usedNative, spoken: text, lang };
}

export function stopPronounce() {
  try {
    window.speechSynthesis?.cancel();
  } catch (e) {
    /* ignore */
  }
}
